import React from 'react';
import { Download, Upload, ArrowLeft, Maximize } from 'lucide-react';

export const ExpandResult = ({ processedImage, originalImage, onBackClick, onNewUpload }) => {

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = 'expanded-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="flex-1 h-full w-full overflow-auto relative z-10 bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="w-full mx-auto py-2 sm:py-3 px-4 sm:px-6 bg-white shadow-md border-b border-blue-100 sticky top-0">
        <div className="flex items-center">
          <button 
            onClick={onBackClick}
            className="mr-2 sm:mr-4 p-1 sm:p-2 rounded-full hover:bg-blue-50 transition-colors text-blue-600"
          >
            <ArrowLeft size={20} className="sm:w-6 sm:h-6" />
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold bg-gradient-to-r from-violet-600 to-blue-500 text-transparent bg-clip-text">
            Image Expander Result
          </h1>
        </div>
      </header>
         
      {/* Main content */}
      <div className="flex flex-col lg:flex-row w-full p-3 sm:p-4 md:p-6 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
        {/* Original Image */}
        <div className="flex-1 flex flex-col">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 mb-3 sm:mb-4 w-full border border-blue-100">
            <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-3 text-gray-800 flex items-center border-b pb-2 sm:pb-3 border-blue-100">
              <span className="mr-2 p-1 rounded-full bg-gray-200 text-gray-700">
                <ArrowLeft size={14} className="sm:w-4 sm:h-4" />
              </span>
              Original Image
            </h2>
            <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-gray-50 rounded-lg border border-blue-100 flex items-center justify-center overflow-hidden">
              {originalImage ? (
                <img 
                  src={originalImage} 
                  alt="Original" 
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-gray-400 text-sm sm:text-base flex items-center justify-center h-full">
                  No original image available
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Arrow between images */}
        <div className="flex lg:hidden items-center justify-center my-2">
          <div className="p-1 sm:p-2 rounded-full bg-gradient-to-r from-violet-600 to-blue-500 text-white">
            <Maximize size={20} className="sm:w-6 sm:h-6" />
          </div>
        </div>
        <div className="hidden lg:flex items-center justify-center">
          <div className="p-2 rounded-full bg-gradient-to-r from-violet-600 to-blue-500 text-white">
            <Maximize size={24} />
          </div>
        </div>
           
        {/* Expanded Image */}
        <div className="flex-1 flex flex-col">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 mb-3 sm:mb-4 w-full border border-blue-100">
            <h2 className="text-base sm:text-lg font-medium mb-2 sm:mb-3 text-gray-800 flex items-center border-b pb-2 sm:pb-3 border-blue-100">
              <span className="mr-2 p-1 rounded-full bg-gradient-to-r from-violet-600 to-blue-500 text-white">
                <Maximize size={14} className="sm:w-4 sm:h-4" />
              </span>
              Expanded Image
            </h2>
            <div className="w-full h-48 sm:h-64 md:h-80 lg:h-96 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border border-blue-100 flex items-center justify-center overflow-hidden relative">
              {processedImage ? (
                <img 
                  src={processedImage} 
                  alt="Expanded" 
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full w-full">
                  <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-blue-500 mb-2 sm:mb-3 md:mb-4"></div>
                  <p className="text-blue-600 font-medium text-sm sm:text-base">Processing image...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
         
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 p-4 sm:p-6 mb-4 sm:mb-6">
        <button
          onClick={handleDownload}
          disabled={!processedImage}
          className="flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-2 sm:py-3 w-full sm:w-auto bg-gradient-to-r from-blue-500 to-indigo-700 text-white rounded-lg hover:from-blue-600 hover:to-indigo-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-colors shadow-md text-sm sm:text-base"
        >
          <Download size={18} className="sm:w-5 sm:h-5" />
          Download Expanded Image
        </button>
           
        <button
          onClick={onNewUpload}
          className="flex items-center justify-center gap-2 px-4 sm:px-6 md:px-8 py-2 sm:py-3 w-full sm:w-auto bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200 border border-gray-300 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
        >
          <Upload size={18} className="sm:w-5 sm:h-5" />
          Upload New Image
        </button>
      </div>
      
      {/* Info Cards */}
      {processedImage && (
        <div className="w-full px-4 sm:px-6 max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-blue-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Resolution</h3>
            <p className="text-base sm:text-lg font-semibold text-blue-700">4K Ultra HD</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-blue-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Expansion Ratio</h3>
            <p className="text-base sm:text-lg font-semibold text-blue-700">2.5x Original Size</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-blue-100">
            <h3 className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Quality</h3>
            <p className="text-base sm:text-lg font-semibold text-blue-700">High Fidelity</p>
          </div>
        </div>
      )}
    </div>
  );
};